/**
 * Tomorrow's auction, read and write (canon APP-003).
 *
 * Three disciplines apply here, and each is a canon rule rather than a preference.
 *
 * **The session decides whose auction this is.** The read carries no parameter at all, and
 * the write carries a deal id and an amount and nothing else — no seller id, no date. Core
 * refuses an unknown field rather than ignoring it, so a client that sent `sellerId` would
 * be told, not quietly believed (`contracts/API.md` § Seller bidding shape).
 *
 * **The day is core's.** Canon § 7 fixes bidding to *tomorrow*, canon § 8 fixes tomorrow to
 * the server's UTC clock. Nothing here computes a display date; the one core sends is the
 * one shown.
 *
 * **Money and rank are core's.** `docs/SECURITY.md` § Credits/bids makes the browser's
 * balance display-only, and the projected rank in the response is core's own count of the
 * bids that beat this one. This module parses those figures; it never derives them.
 */
import { apiRequest, apiRequestWithStatus } from "./client";
import { parseSellerCredit } from "./desk";
import {
  INVALID,
  asRecord,
  malformed,
  nonEmptyString,
  requiredCount,
} from "./parse";
import type {
  AuctionStatus,
  HonorableMentionOffer,
  PlacedBid,
  PurchasedHonorableMention,
  PurchasedMentionResult,
  RankingDisclosure,
  SellerAuctionDeal,
  SellerAuctionSummary,
  BiddingState,
  SellerAuctionView,
  SellerStandingBid,
} from "./types";

const AUCTION_STATUSES = new Set<string>(["open", "finalizing", "finalized"]);

/**
 * An unrecognised auction status fails the parse instead of degrading to a default.
 *
 * The status decides whether a bid form is offered at all. Treating an unknown value as
 * `open` would invite a bid core will refuse; treating it as closed would hide a form that
 * should be there. Neither is honest, so the panel says it could not read the answer.
 */
function isAuctionStatus(value: string): value is AuctionStatus {
  return AUCTION_STATUSES.has(value);
}

/**
 * The sponsored disclosure. Required, not optional.
 *
 * Canon § 12 makes the sponsored nature of bid-driven placement something the founder must
 * see stated, and `contracts/API.md` ships the sentence in the response so no surface can
 * quietly drop it. A body without it is not a body this app may render.
 */
function parseDisclosure(value: unknown): RankingDisclosure | null {
  const row = asRecord(value);
  if (row === null) return null;
  const code = nonEmptyString(row.code);
  const message = nonEmptyString(row.message);
  if (code === null || message === null) return null;
  return { sponsored: row.sponsored === true, code, message };
}

/**
 * A standing bid, or `null` when this deal has none.
 *
 * A rank of zero is refused along with a missing one: canon § 7 ranks from position 1, so
 * `0` is not a position core would send and is not a position to render.
 */
function parseStandingBid(value: unknown): SellerStandingBid | null | typeof INVALID {
  if (value === null || value === undefined) return null;
  const row = asRecord(value);
  if (row === null) return INVALID;

  const amountCents = requiredCount(row.amountCents);
  const projectedRank = requiredCount(row.projectedRank);
  const qualifiedAt = nonEmptyString(row.qualifiedAt);
  if (
    amountCents === INVALID ||
    projectedRank === INVALID ||
    projectedRank < 1 ||
    qualifiedAt === null
  ) {
    return INVALID;
  }

  return {
    amountCents,
    qualifiedAt,
    projectedRank,
    // Anything other than a literal `true` is not a claim that this bid is winning. Whether
    // a position is inside the ten is core's decision (canon § 7), never an inference from
    // the rank this app happens to be holding.
    withinTop10: row.withinTop10 === true,
  };
}

function parseAuctionDeal(value: unknown): SellerAuctionDeal | null {
  const row = asRecord(value);
  if (row === null) return null;

  const dealId = nonEmptyString(row.dealId);
  const slug = nonEmptyString(row.slug);
  const productName = nonEmptyString(row.productName);
  const minimumBidCents = requiredCount(row.minimumBidCents);
  if (
    dealId === null ||
    slug === null ||
    productName === null ||
    minimumBidCents === INVALID
  ) {
    return null;
  }

  const bid = parseStandingBid(row.bid);
  if (bid === INVALID) return null;

  return { dealId, slug, productName, bid, minimumBidCents };
}

function parseAuctionSummary(value: unknown): SellerAuctionSummary | null | typeof INVALID {
  // Canon `contracts/API.md`: `auction: null` before finalization has opened tomorrow's row
  // is the normal state, not an error. The panel says "not open yet" rather than failing.
  if (value === null || value === undefined) return null;
  const row = asRecord(value);
  if (row === null) return INVALID;

  const status = nonEmptyString(row.status);
  const opensAt = nonEmptyString(row.opensAt);
  const closesAt = nonEmptyString(row.closesAt);
  const minBidCents = requiredCount(row.minBidCents);
  if (
    status === null ||
    !isAuctionStatus(status) ||
    opensAt === null ||
    closesAt === null ||
    minBidCents === INVALID
  ) {
    return INVALID;
  }

  const capacity = asRecord(row.capacity);
  if (capacity === null) return INVALID;
  const top10 = requiredCount(capacity.top10);
  const standingBids = requiredCount(capacity.standingBids);
  if (top10 === INVALID || standingBids === INVALID) return INVALID;

  return { status, opensAt, closesAt, minBidCents, capacity: { top10, standingBids } };
}

/**
 * One mention this founder already holds for the day.
 *
 * Every field is required. This row's whole job is to say "you already have this deal
 * listed for tomorrow" in place of offering to sell it again, and a row missing its deal id
 * could not do that — it would leave the buy control on screen for a deal core will refuse.
 */
function parsePurchasedMention(value: unknown): PurchasedHonorableMention | null {
  const row = asRecord(value);
  if (row === null) return null;

  const placementId = nonEmptyString(row.placementId);
  const dealId = nonEmptyString(row.dealId);
  const slug = nonEmptyString(row.slug);
  const productName = nonEmptyString(row.productName);
  const purchasedAt = nonEmptyString(row.purchasedAt);
  if (
    placementId === null ||
    dealId === null ||
    slug === null ||
    productName === null ||
    purchasedAt === null
  ) {
    return null;
  }

  return { placementId, dealId, slug, productName, purchasedAt };
}

/**
 * The Honorable Mention offer. Required, not optional.
 *
 * `contracts/API.md` § Seller bidding shape sends this block on every seller auction read,
 * populated whether or not `auction` is null. Treating a missing block as "no offer" would
 * silently hide a purchasable placement from a founder and give no sign that anything was
 * wrong, so a body without it is refused the same way a body without the disclosure is.
 *
 * `priceCents` is read rather than assumed. Canon § 10 fixes the price at $1, but it is
 * core's rate card: a constant here would be a second copy, and the two would eventually
 * disagree about what a founder is being charged.
 */
function parseHonorableMentionOffer(value: unknown): HonorableMentionOffer | null {
  const row = asRecord(value);
  if (row === null) return null;

  const priceCents = requiredCount(row.priceCents);
  const disclosure = parseDisclosure(row.disclosure);
  if (priceCents === INVALID || disclosure === null) return null;

  if (!Array.isArray(row.purchased)) return null;
  const purchased: PurchasedHonorableMention[] = [];
  for (const entry of row.purchased) {
    const mention = parsePurchasedMention(entry);
    // A dropped row would put the buy control back on a deal that is already listed, which
    // is the one mistake this list exists to prevent. The panel says it did not load.
    if (mention === null) return null;
    purchased.push(mention);
  }

  return { priceCents, disclosure, purchased };
}

/**
 * Canon § 6's bidding gate (decision `D-011`).
 *
 * Required rather than optional. A missing `bidding` object would default to *something*,
 * and either default is wrong in a way the founder pays for: assuming open shows a bid form
 * that every submission will refuse, and assuming closed hides bidding from a marketplace
 * that is running an auction. Failing the parse says "this did not load", which is true.
 */
function parseBiddingState(value: unknown): BiddingState | null {
  const row = asRecord(value);
  if (row === null) return null;

  const eligibleDealsRequired = requiredCount(row.eligibleDealsRequired);
  if (eligibleDealsRequired === INVALID) return null;

  const open = row.open === true;
  const reason = row.reason === "cold_start" ? "cold_start" : null;
  // The two halves have to agree. An open auction with a reason, or a closed one without,
  // is a shape neither this app nor core has a meaning for.
  if (open === (reason !== null)) return null;

  return { open, reason, eligibleDealsRequired };
}

/** Returns `null` when the body is not a seller auction view. */
export function parseSellerAuction(body: unknown): SellerAuctionView | null {
  const row = asRecord(body);
  if (row === null) return null;

  const displayDate = nonEmptyString(row.displayDate);
  const serverTime = nonEmptyString(row.serverTime);
  const disclosure = parseDisclosure(row.disclosure);
  if (displayDate === null || serverTime === null || disclosure === null) return null;

  const auction = parseAuctionSummary(row.auction);
  if (auction === INVALID) return null;

  const bidding = parseBiddingState(row.bidding);
  if (bidding === null) return null;

  if (!Array.isArray(row.deals)) return null;
  const deals: SellerAuctionDeal[] = [];
  for (const entry of row.deals) {
    const deal = parseAuctionDeal(entry);
    // One malformed row fails the panel rather than being skipped. A founder shown a short
    // list would reasonably conclude a deal had lost its eligibility to bid; "this didn't
    // load" is the true statement, and it is the one that gets reported.
    if (deal === null) return null;
    deals.push(deal);
  }

  const honorableMentions = parseHonorableMentionOffer(row.honorableMentions);
  if (honorableMentions === null) return null;

  return { displayDate, serverTime, disclosure, auction, bidding, deals, honorableMentions };
}

/**
 * Returns `null` when the body is not an accepted bid.
 *
 * `created` comes from the status core answered with — `201` for a new bid, `200` for a
 * raise — so the desk never has to compare amounts to work out which happened.
 */
export function parsePlacedBid(status: number, body: unknown): PlacedBid | null {
  const row = asRecord(body);
  if (row === null) return null;

  const bid = asRecord(row.bid);
  if (bid === null) return null;

  const id = nonEmptyString(bid.id);
  const dealId = nonEmptyString(bid.dealId);
  const displayDate = nonEmptyString(bid.displayDate);
  const qualifiedAt = nonEmptyString(bid.qualifiedAt);
  const amountCents = requiredCount(bid.amountCents);
  const projectedRank = requiredCount(bid.projectedRank);
  if (
    id === null ||
    dealId === null ||
    displayDate === null ||
    qualifiedAt === null ||
    amountCents === INVALID ||
    projectedRank === INVALID ||
    projectedRank < 1
  ) {
    return null;
  }

  // The same credit shape `GET /v1/seller/credit` returns, and required for the same
  // reason: canon `AGENTS.md` § 7 does not allow a balance on screen without the non-cash
  // terms that travel with it.
  const credit = parseSellerCredit(row);
  if (credit === null) return null;

  return {
    created: status === 201,
    id,
    dealId,
    displayDate,
    amountCents,
    qualifiedAt,
    projectedRank,
    withinTop10: bid.withinTop10 === true,
    credit,
  };
}

/** Tomorrow's auction as it stands for the signed-in seller. */
export async function fetchSellerAuction(): Promise<SellerAuctionView> {
  const auction = parseSellerAuction(await apiRequest<unknown>("/v1/seller/auction/tomorrow"));
  if (auction === null) throw malformed();
  return auction;
}

/**
 * Places or raises a bid on tomorrow's auction.
 *
 * The body is exactly `{ dealId, amountCents }`. Everything else — which auction, which
 * seller, the minimum, whether the credit is there, what the bid costs — is core's decision
 * (canon § 7, `docs/SECURITY.md` § Credits/bids), and a refusal comes back as a code this
 * app words for a founder rather than a state it could have predicted.
 */
export async function placeSellerBid(input: {
  dealId: string;
  amountCents: number;
}): Promise<PlacedBid> {
  const { status, body } = await apiRequestWithStatus<unknown>("/v1/seller/bids", {
    method: "POST",
    body: { dealId: input.dealId, amountCents: input.amountCents },
  });
  const placed = parsePlacedBid(status, body);
  if (placed === null) throw malformed();
  return placed;
}

/**
 * Returns `null` when the body is not an accepted Honorable Mention.
 *
 * `displayDate` and `priceCents` are required even though canon § 10 already fixes both:
 * the confirmation tells a founder which day they just bought and what it cost, and both of
 * those sentences must quote core rather than restate a rule this app is holding a copy of.
 */
export function parsePurchasedMentionResult(body: unknown): PurchasedMentionResult | null {
  const row = asRecord(body);
  if (row === null) return null;

  const mention = asRecord(row.honorableMention);
  if (mention === null) return null;

  const placementId = nonEmptyString(mention.placementId);
  const dealId = nonEmptyString(mention.dealId);
  const displayDate = nonEmptyString(mention.displayDate);
  const purchasedAt = nonEmptyString(mention.purchasedAt);
  const priceCents = requiredCount(mention.priceCents);
  if (
    placementId === null ||
    dealId === null ||
    displayDate === null ||
    purchasedAt === null ||
    priceCents === INVALID
  ) {
    return null;
  }

  const disclosure = parseDisclosure(row.disclosure);
  if (disclosure === null) return null;

  // The same credit shape `GET /v1/seller/credit` returns, and required for the same
  // reason: canon `AGENTS.md` § 7 does not allow a balance on screen without the non-cash
  // terms that travel with it.
  const credit = parseSellerCredit(row);
  if (credit === null) return null;

  return {
    mention: { placementId, dealId, displayDate, priceCents, purchasedAt },
    disclosure,
    credit,
  };
}

/**
 * Buys one Honorable Mention for tomorrow's display day.
 *
 * The body is exactly `{ dealId }`. Canon § 10 fixes the price and canon § 8 fixes the day,
 * so there is no amount and no date for this app to send — and core refuses `amountCents`,
 * `displayDate` or `sellerId` rather than ignoring them. Whether the deal is eligible,
 * whether it already holds a position and whether the credit is there are all core's
 * decisions, and its refusal is what the founder is shown.
 */
export async function purchaseHonorableMention(input: {
  dealId: string;
}): Promise<PurchasedMentionResult> {
  const body = await apiRequest<unknown>("/v1/seller/honorable-mentions", {
    method: "POST",
    body: { dealId: input.dealId },
  });
  const purchased = parsePurchasedMentionResult(body);
  if (purchased === null) throw malformed();
  return purchased;
}
